import { PageHero } from "@/components/site/PageHero";
import { SectionLabel, RevealLines, FadeUp } from "@/components/site/motion";
import { CapabilityList } from "@/components/services/CapabilityList";
import { CTA } from "@/components/site/CTA";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { PILLARS, PHASES, STACK, FAQ } from "@/lib/content";

const PillarDetail = ({ p }) => (
  <article data-testid={`service-pillar-${p.id}`} className="grid gap-10 border-t border-cream/10 py-16 lg:grid-cols-12 lg:py-24">
    <div className="lg:col-span-5">
      <div className="lg:sticky lg:top-28">
        <span className="font-mono text-xs text-skaris-sky">{p.n} / 03</span>
        <h3 className="mt-4 font-display text-4xl font-semibold uppercase leading-[0.95] tracking-tight text-cream md:text-6xl">{p.title}</h3>
        <div className="mt-8 aspect-[4/3] overflow-hidden rounded-[22px]">
          <img src={p.img} alt={p.title} className="duotone h-full w-full object-cover transition-transform duration-[1.4s] hover:scale-105" />
        </div>
      </div>
    </div>
    <div className="lg:col-span-6 lg:col-start-7 lg:pt-12">
      <FadeUp as="p" className="font-display text-2xl font-medium leading-snug text-cream md:text-3xl">{p.short}</FadeUp>
      <FadeUp as="p" delay={0.1} className="mt-6 font-body text-base leading-relaxed text-cream/75">{p.long}</FadeUp>
      <ul className="mt-10 divide-y divide-cream/10 border-y border-cream/10">
        {p.points.map((pt, i) => (
          <FadeUp as="li" key={pt} delay={i * 0.06} className="flex items-center justify-between py-5 font-body text-base text-cream">
            {pt}<span className="font-mono text-[11px] text-cream/50">0{i + 1}</span>
          </FadeUp>
        ))}
      </ul>
    </div>
  </article>
);

const Engagement = () => (
  <section data-testid="services-engagement" className="bg-cream py-24 text-skaris-ink md:py-32">
    <div className="mx-auto max-w-[1440px] px-6 md:px-10">
      <SectionLabel n="(03)" className="!text-skaris-accent [&_span:first-child]:!bg-skaris-accent [&_span:nth-child(2)]:!text-skaris-ink/60">Engagement model</SectionLabel>
      <RevealLines inView as="h2" lines={["Priced per phase.", "Known at every step."]}
        className="mt-6 font-display text-4xl font-semibold uppercase leading-[0.95] tracking-[-0.03em] text-skaris-navy sm:text-5xl lg:text-7xl" />
      <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {PHASES.map((ph, i) => (
          <FadeUp key={ph.n} delay={i * 0.07} data-testid={`phase-${ph.n}`}
            className="group flex min-h-[260px] flex-col justify-between rounded-[20px] border border-skaris-navy/15 bg-white/50 p-6 transition-[background-color,transform] duration-500 hover:-translate-y-1.5 hover:bg-skaris-navy">
            <span className="font-mono text-xs text-skaris-accent transition-colors group-hover:text-cream/70">{ph.n}</span>
            <div>
              <h3 className="font-display text-2xl font-semibold text-skaris-navy transition-colors group-hover:text-cream">{ph.title}</h3>
              <p className="mt-2 font-body text-sm text-skaris-ink/75 transition-colors group-hover:text-cream/80">{ph.body}</p>
              <p className="mt-5 font-mono text-[11px] uppercase tracking-[0.15em] text-skaris-navy/70 transition-colors group-hover:text-skaris-glow">{ph.note}</p>
            </div>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

const StackAndFaq = () => (
  <section data-testid="services-faq" className="py-24 md:py-32">
    <div className="mx-auto grid max-w-[1440px] gap-16 px-6 md:px-10 lg:grid-cols-12">
      <div className="lg:col-span-5">
        <SectionLabel n="(04)">Stack we ship with</SectionLabel>
        <div className="mt-8 flex flex-wrap gap-2.5">
          {STACK.map((s, i) => (
            <FadeUp key={s} delay={i * 0.03} className="rounded-full border border-cream/15 px-4 py-2 font-body text-sm text-cream/85 transition-colors duration-300 hover:border-skaris-sky hover:text-cream">{s}</FadeUp>
          ))}
        </div>
      </div>
      <div className="lg:col-span-6 lg:col-start-7">
        <SectionLabel n="(05)">FAQ</SectionLabel>
        <Accordion type="single" collapsible className="mt-6">
          {FAQ.map((f, i) => (
            <AccordionItem key={i} value={`q${i}`} className="border-cream/10">
              <AccordionTrigger data-testid={`faq-trigger-${i}`} className="py-6 text-left font-display text-xl font-medium text-cream hover:no-underline">{f.q}</AccordionTrigger>
              <AccordionContent className="font-body text-base leading-relaxed text-cream/75">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </div>
  </section>
);

export default function Services() {
  return (
    <main data-testid="services-page">
      <PageHero
        testid="services-hero"
        label="Services"
        lines={["Build it.", "Transform it.", "Make it smart."]}
        intro="IT services, digital transformation and AI transformation — delivered by one lean team that owns the outcome end-to-end."
      />
      <section data-testid="services-pillars" className="pt-24 md:pt-32">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10">
          <SectionLabel n="(01)">Three pillars</SectionLabel>
          {PILLARS.map((p) => <PillarDetail key={p.id} p={p} />)}
        </div>
      </section>
      <CapabilityList />
      <Engagement />
      <StackAndFaq />
      <CTA />
    </main>
  );
}
